import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Pencil,
  Save,
  XCircle,
  Package,
  Tag,
  Hash,
  Ruler,
  Weight,
  AlertTriangle,
  Thermometer,
  Layers,
  Truck,
  Calendar,
  Building2,
} from 'lucide-react';
import { businessesService } from '@services/businesses.service';
import { PermissionGate } from '@components/PermissionGate';
import { cn } from '@lib/utils';

interface Props {
  businessId: string;
  product: any;
  onClose: () => void;
}

// Prices are NUMERIC(15,2) KES (not cents) — display as-is.
const formatKes = (amount?: number | null) => {
  if (amount == null) return '—';
  return `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
};

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-KE', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// ── Reusable presentation pieces ──────────────────────────────────────────────

const Field = ({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ElementType;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-[var(--border-subtle)] last:border-0">
    {Icon && <Icon className="w-4 h-4 text-[var(--text-muted)] mt-0.5 flex-shrink-0" strokeWidth={1.5} />}
    <div className="flex-1 min-w-0">
      <p className="text-[12px] text-[var(--text-muted)] mb-0.5">{label}</p>
      <div className="text-[14px] text-[var(--text-primary)] break-words">{value ?? <span className="text-[var(--text-muted)]">—</span>}</div>
    </div>
  </div>
);

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-2xl border border-[var(--border-subtle)] p-4">
    <h4 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-muted)] mb-2">{title}</h4>
    <div>{children}</div>
  </div>
);

const Bool = ({ value }: { value?: boolean | null }) => {
  if (value == null) return <span className="text-[var(--text-muted)]">—</span>;
  return value ? (
    <span className="rounded-full px-2.5 py-0.5 text-[12px] font-semibold bg-[var(--state-success-bg)] text-[var(--state-success)]">Yes</span>
  ) : (
    <span className="rounded-full px-2.5 py-0.5 text-[12px] font-semibold bg-[var(--bg-base)] text-[var(--text-muted)]">No</span>
  );
};

// ── Editable form fields (controlled) ─────────────────────────────────────────

const inputClass =
  'w-full rounded-2xl border border-[var(--border-default)] bg-white px-4 py-2.5 text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--border-strong)] transition-colors duration-200';

const labelClass = 'block text-[12px] font-semibold uppercase tracking-[0.08em] text-[var(--text-primary)] mb-1.5';

const TextField = ({
  label, value, onChange, placeholder, type = 'text',
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputClass}
    />
  </div>
);

const NumberField = ({
  label, value, onChange, placeholder, step,
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; step?: string }) => (
  <div>
    <label className={labelClass}>{label}</label>
    <input
      type="number"
      step={step}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={inputClass}
    />
  </div>
);

const ToggleField = ({
  label, value, onChange,
}: { label: string; value: boolean; onChange: (v: boolean) => void }) => (
  <label className="flex items-center justify-between gap-3 cursor-pointer py-1.5">
    <span className="text-[13px] text-[var(--text-primary)]">{label}</span>
    <input
      type="checkbox"
      checked={value}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 accent-[var(--brand-forest)]"
    />
  </label>
);

// ── Main modal ────────────────────────────────────────────────────────────────

export const ProductDetailModal = ({ businessId, product, onClose }: Props) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const initial = useMemo(() => ({
    name: product.name ?? '',
    sku: product.sku ?? '',
    barcode: product.barcode ?? '',
    shortDescription: product.shortDescription ?? '',
    description: product.description ?? '',
    basePrice: product.basePrice != null ? String(product.basePrice) : '',
    compareAtPrice: product.compareAtPrice != null ? String(product.compareAtPrice) : '',
    costPrice: product.costPrice != null ? String(product.costPrice) : '',
    weightGrams: product.weightGrams != null ? String(product.weightGrams) : '',
    lengthMm: product.lengthMm != null ? String(product.lengthMm) : '',
    widthMm: product.widthMm != null ? String(product.widthMm) : '',
    heightMm: product.heightMm != null ? String(product.heightMm) : '',
    isTaxable: product.isTaxable ?? false,
    requiresShipping: product.requiresShipping ?? false,
    isFragile: product.isFragile ?? false,
    isHazardous: product.isHazardous ?? false,
    isStackable: product.isStackable ?? true,
  }), [product]);

  const [form, setForm] = useState(initial);

  useEffect(() => {
    if (!editing) setForm(initial);
  }, [editing, initial]);

  const updateMutation = useMutation({
    mutationFn: () => {
      const toLong = (s: string) => (s.trim() === '' ? undefined : Number(s));
      const payload: Record<string, unknown> = {
        name: form.name || undefined,
        sku: form.sku || undefined,
        barcode: form.barcode || undefined,
        shortDescription: form.shortDescription || undefined,
        description: form.description || undefined,
        basePrice: toLong(form.basePrice),
        compareAtPrice: toLong(form.compareAtPrice),
        costPrice: toLong(form.costPrice),
        weightGrams: toLong(form.weightGrams),
        lengthMm: toLong(form.lengthMm),
        widthMm: toLong(form.widthMm),
        heightMm: toLong(form.heightMm),
        isTaxable: form.isTaxable,
        requiresShipping: form.requiresShipping,
        isFragile: form.isFragile,
        isHazardous: form.isHazardous,
        isStackable: form.isStackable,
      };
      Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
      return businessesService.updateProduct(businessId, product.branchId, product.id, payload);
    },
    onSuccess: () => {
      setSaveError(null);
      setEditing(false);
      queryClient.invalidateQueries({ queryKey: ['business-products', businessId] });
    },
    onError: (err: unknown) => {
      setSaveError((err as { message?: string })?.message ?? 'Failed to update product.');
    },
  });

  const images: string[] = product.images ?? (product.primaryImage ? [product.primaryImage] : []);

  return (
    <div
      className="fixed inset-0 bg-[rgba(15,61,30,0.50)] z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-lift max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-[var(--border-subtle)]">
          <div className="flex items-center gap-3 min-w-0">
            {product.primaryImage ? (
              <img
                src={product.primaryImage}
                alt={product.name}
                className="w-12 h-12 rounded-2xl object-cover bg-[var(--bg-base)] flex-shrink-0"
              />
            ) : (
              <div className="w-12 h-12 rounded-2xl bg-[var(--bg-base)] flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-[var(--text-muted)]" strokeWidth={1.5} />
              </div>
            )}
            <div className="min-w-0">
              <h3 className="font-display text-[24px] font-medium tracking-[-0.02em] text-[var(--text-primary)] truncate">
                {product.name}
              </h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className={cn(
                  'rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
                  product.isActive ? 'bg-[var(--state-success-bg)] text-[var(--state-success)]' : 'bg-[var(--bg-base)] text-[var(--text-muted)]',
                )}>
                  {product.isActive ? 'Active' : 'Suspended'}
                </span>
                {product.productType && (
                  <span className="text-[12px] text-[var(--text-muted)] capitalize">{product.productType}</span>
                )}
                {product.sku && <span className="text-[12px] text-[var(--text-muted)]">SKU: {product.sku}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!editing ? (
              <PermissionGate permission="products.update">
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-forest)] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[var(--brand-forest)] transition-all duration-200"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Edit
                </button>
              </PermissionGate>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => { setEditing(false); setSaveError(null); }}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--border-subtle)] bg-white px-4 py-2 text-[13px] font-semibold text-[var(--text-primary)] hover:border-[var(--border-default)] hover:bg-[var(--bg-base)] transition-all duration-200"
                >
                  <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} />
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={updateMutation.isPending}
                  onClick={() => updateMutation.mutate()}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--gradient-to)] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[var(--text-accent)] transition-all duration-200 disabled:opacity-40"
                >
                  <Save className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-[var(--bg-base)] flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-[var(--text-muted)]" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {saveError && (
            <div className="rounded-2xl border border-[var(--state-danger-border)] bg-[var(--state-danger-bg)] px-4 py-3">
              <p className="text-[13px] text-[var(--state-danger)]">{saveError}</p>
            </div>
          )}

          {/* Image gallery (read-only for now; image management is a separate API) */}
          {images.length > 0 && (
            <div className="flex gap-3 overflow-x-auto pb-1">
              {images.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`${product.name} ${i + 1}`}
                  className="w-24 h-24 rounded-2xl object-cover bg-[var(--bg-base)] flex-shrink-0"
                />
              ))}
            </div>
          )}

          {!editing ? (
            // ── VIEW MODE ─────────────────────────────────────────────────────
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Section title="Identity">
                <Field icon={Tag} label="Name" value={product.name} />
                <Field icon={Hash} label="SKU" value={product.sku} />
                <Field icon={Hash} label="Barcode" value={product.barcode} />
                <Field icon={Hash} label="Slug" value={product.slug} />
                <Field icon={Building2} label="Branch" value={product.branchName ?? product.branchId} />
              </Section>

              <Section title="Pricing & Tax">
                <Field label="Base Price" value={formatKes(product.basePrice)} />
                <Field label="Compare-at Price" value={formatKes(product.compareAtPrice)} />
                <Field label="Cost Price" value={formatKes(product.costPrice)} />
                <Field label="Taxable" value={<Bool value={product.isTaxable} />} />
                <Field label="Tax Override" value={product.taxTypeOverride} />
                <Field label="KRA Item Code" value={product.kraItemCode} />
              </Section>

              <Section title="Stock">
                <Field icon={Package} label="Total Available"
                  value={typeof product.totalStock === 'number' ? product.totalStock.toLocaleString() : '—'} />
                <Field label="Min Order Qty" value={product.minOrderQuantity} />
                <Field label="Max Order Qty" value={product.maxOrderQuantity} />
                <Field label="Sold by Weight" value={<Bool value={product.soldByWeight} />} />
                <Field label="Unit" value={product.unitLabel ?? product.unitOfMeasure} />
              </Section>

              <Section title="Dimensions & Weight">
                <Field icon={Weight} label="Weight (g)" value={product.weightGrams} />
                <Field icon={Ruler} label="Length (mm)" value={product.lengthMm} />
                <Field icon={Ruler} label="Width (mm)" value={product.widthMm} />
                <Field icon={Ruler} label="Height (mm)" value={product.heightMm} />
              </Section>

              <Section title="Logistics">
                <Field icon={Truck} label="Requires Shipping" value={<Bool value={product.requiresShipping} />} />
                <Field icon={AlertTriangle} label="Fragile" value={<Bool value={product.isFragile} />} />
                <Field label="Fragility Level" value={product.fragilityLevel} />
                <Field icon={Thermometer} label="Temp. Controlled" value={<Bool value={product.requiresTemperatureControl} />} />
                <Field label="Temp. Range" value={product.temperatureRange} />
                <Field icon={AlertTriangle} label="Hazardous" value={<Bool value={product.isHazardous} />} />
                <Field label="Hazard Class" value={product.hazardClass} />
                <Field icon={Layers} label="Stackable" value={<Bool value={product.isStackable} />} />
              </Section>

              <Section title="Audit">
                <Field icon={Calendar} label="Created" value={formatDate(product.createdAt)} />
                <Field icon={Calendar} label="Updated" value={formatDate(product.updatedAt)} />
              </Section>

              {(product.shortDescription || product.description) && (
                <div className="md:col-span-2">
                  <Section title="Description">
                    {product.shortDescription && (
                      <p className="text-[14px] text-[var(--text-secondary)] mb-2 italic">{product.shortDescription}</p>
                    )}
                    {product.description && (
                      <p className="text-[14px] text-[var(--text-secondary)] whitespace-pre-line">{product.description}</p>
                    )}
                  </Section>
                </div>
              )}
            </div>
          ) : (
            // ── EDIT MODE ─────────────────────────────────────────────────────
            <div className="space-y-5">
              <Section title="Identity">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                  <TextField label="SKU" value={form.sku} onChange={(v) => setForm({ ...form, sku: v })} />
                  <TextField label="Barcode" value={form.barcode} onChange={(v) => setForm({ ...form, barcode: v })} />
                </div>
                <div className="grid grid-cols-1 gap-4 pt-2">
                  <TextField
                    label="Short Description"
                    value={form.shortDescription}
                    onChange={(v) => setForm({ ...form, shortDescription: v })}
                  />
                  <div>
                    <label className={labelClass}>Description</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={4}
                      className={cn(inputClass, 'resize-none')}
                    />
                  </div>
                </div>
              </Section>

              <Section title="Pricing (KES)">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                  <NumberField label="Base Price" value={form.basePrice} onChange={(v) => setForm({ ...form, basePrice: v })} />
                  <NumberField label="Compare-at Price" value={form.compareAtPrice} onChange={(v) => setForm({ ...form, compareAtPrice: v })} />
                  <NumberField label="Cost Price" value={form.costPrice} onChange={(v) => setForm({ ...form, costPrice: v })} />
                </div>
              </Section>

              <Section title="Dimensions & Weight">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
                  <NumberField label="Weight (g)" value={form.weightGrams} onChange={(v) => setForm({ ...form, weightGrams: v })} />
                  <NumberField label="Length (mm)" value={form.lengthMm} onChange={(v) => setForm({ ...form, lengthMm: v })} />
                  <NumberField label="Width (mm)" value={form.widthMm} onChange={(v) => setForm({ ...form, widthMm: v })} />
                  <NumberField label="Height (mm)" value={form.heightMm} onChange={(v) => setForm({ ...form, heightMm: v })} />
                </div>
              </Section>

              <Section title="Flags">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 pt-2">
                  <ToggleField label="Taxable" value={form.isTaxable} onChange={(v) => setForm({ ...form, isTaxable: v })} />
                  <ToggleField label="Requires Shipping" value={form.requiresShipping} onChange={(v) => setForm({ ...form, requiresShipping: v })} />
                  <ToggleField label="Fragile" value={form.isFragile} onChange={(v) => setForm({ ...form, isFragile: v })} />
                  <ToggleField label="Hazardous" value={form.isHazardous} onChange={(v) => setForm({ ...form, isHazardous: v })} />
                  <ToggleField label="Stackable" value={form.isStackable} onChange={(v) => setForm({ ...form, isStackable: v })} />
                </div>
              </Section>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
